import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button as MuiButton, Divider, Stack, TextField } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import _ from 'lodash';
import moment from 'moment/moment';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';

import { getAllProductProductsNotificationData, getWarehouseProductBatchesNotificationData } from '../../../../../../../core/utils/notification_utils';
import useNotification from '../../../../../../../core/hooks/use_notification';
import { getWarehouseProductBatchesAction, invalidateProductBatches } from '../../../../../../../features/inventory/get_warehouse_product_batches';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT } from '../../../../../../../core/constants';
import { Button } from '../../../../../../common/button';
import AutocompleteMultipleSearchOnType from '../../../../../../common/autocomplete';
import { ApiRequestStatusEnum } from '../../../../../../../core/enums/common/api';
import { getAllProductsAction } from '../../../../../../../features/product/get_all_products';

const AddProductsDialog = ({ formik, warehouseId, text, allProducts = false }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const quantityRef = useRef(null);

    const mapProductsOptions = useCallback((data) => {
        if (allProducts) {
            return data?.map(resource => ({
                key: resource?.id,
                value: resource?.id,
                label: resource?.name,
                secondaryLabel: '',
                details: {
                    name: resource?.name,
                    productId: resource?.id,
                    productBatchId: null
                }
            }));
        } else {
            return data?.map(resource => ({
                key: resource?.product?.id + (resource?.productBatchId || null),
                value: resource?.product?.id + '_' + (resource?.productBatchId || null),
                label: resource?.product?.name,
                secondaryLabel: (resource?.productBatchId + ', ' || '') + 'data przydatności: ' + moment(resource?.expiryDate, MISC_DATE_ISO_DATE_ONLY_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) + ', max liczba szt.: ' + resource?.quantity,
                details: {
                    name: resource?.product?.name,
                    productId: resource?.product?.id,
                    productBatchId: resource?.productBatchId
                },
                originalQuantity: resource?.quantity
            }));
        }
    }, [allProducts]);

    const [open, setOpen] = useState(false);
    const [productsOptions, setProductsOptions] = useState([]);
    const [loadingProductsOptions, setLoadingProductsOptions] = useState(false);
    const [chosenProducts, setChosenProducts] = useState([]);

    const onClose = () => {
        setOpen(false);
        dispatch(invalidateProductBatches());
        setChosenProducts([]);
        setProductsOptions([]);
    };

    const onProductsInputChange = (product) => {
        if (product.length >= 3) {
            setProductsOptions([]);
            setLoadingProductsOptions(true);

            getProductsDebounced(product);
        }
    };

    const getProductsDebounced = _.debounce((query) => {
        if (allProducts) {
            dispatch(getAllProductsAction({ query }))
                .then(response => {
                    showErrorMessage(getAllProductProductsNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        const newProductsOptions = mapProductsOptions(response?.payload).concat(chosenProducts);
                        const newDistinctProductsOptions = [...new Map(newProductsOptions.map((option) => [option['value'], option])).values()];
                        setProductsOptions(newDistinctProductsOptions);
                    }
                    setLoadingProductsOptions(false);
                });
        } else {
            dispatch(getWarehouseProductBatchesAction({ sourceWarehouseId: warehouseId, query: { query } }))
                .then(response => {
                    showErrorMessage(getWarehouseProductBatchesNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        const newProductOptions = mapProductsOptions(response?.payload).concat(chosenProducts);
                        const newDistinctProductOptions = [...new Map(newProductOptions.map((option) => [option['value'], option])).values()];
                        setProductsOptions(newDistinctProductOptions);
                    }
                    setLoadingProductsOptions(false);
                });
        }
    }, 600);

    const onAddProducts = () => {
        if (chosenProducts.length === 0) {
            onClose();
            return;
        }

        const positions = formik?.values?.positions || [];
        const productsForValidation = formik?.values?.productsForValidation || [];

        chosenProducts.forEach(product => {
            const lastItem = positions[positions.length - 1];
            const index = positions?.findIndex(p => (p?.productId === product?.details?.productId) && (p?.productBatchId === (product?.details?.productBatchId || null)));

            if (index === -1) {
                positions.push({
                    ...{
                        ...product?.details,
                        quantity: parseInt(quantityRef?.current?.value) || 0,
                    },
                    position: positions.length ? lastItem.position + 1 : 0
                });
            }

            if (product?.details?.productBatchId) {
                const productIndexValidation = productsForValidation?.findIndex(p => (p?.product?.id === product?.details?.productId) && (p?.productBatchId === product?.details?.productBatchId));

                if (productIndexValidation === -1) {
                    productsForValidation.push({
                        productBatchId: product?.details?.productBatchId,
                        productId: product?.details?.productId,
                        quantity: product?.originalQuantity
                    });
                }
            }
        });

        formik.setFieldValue('productsForValidation', productsForValidation);
        formik.setFieldValue('positions', positions);
        onClose();
    };

    return (
        <>
            <MuiButton
                variant="text"
                startIcon={ <AddCircleIcon color="primary"/> }
                onClick={ () => setOpen(true) }
                sx={ { fontWeight: 600, color: 'text.primary' } }
            >
                { text }
            </MuiButton>

            <Dialog PaperProps={ { sx: { maxHeight: 800 } } } open={ open } onClose={ onClose } fullWidth maxWidth="md">
                <DialogTitle sx={ { fontWeight: 'bold' } }>Dodaj produkty</DialogTitle>
                <Divider/>

                <DialogContent>
                    <Stack spacing={ 4 } sx={ { py: 1 } }>
                        {/* Elastyczny CSS Grid zapewniający równe ułożenie */ }
                        <Box sx={ {
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '150px 1fr' },
                            gap: 3,
                            alignItems: 'start'
                        } }>
                            <TextField
                                name="quantity"
                                label="Liczba szt."
                                type="number"
                                inputRef={ quantityRef }
                                defaultValue={ 1 }
                                inputProps={ { min: 1 } }
                            />
                            <AutocompleteMultipleSearchOnType
                                options={ productsOptions }
                                value={ chosenProducts }
                                setValue={ (newValue) => setChosenProducts(newValue) }
                                label="Wyszukaj i wybierz produkty (min. 3 litery)"
                                onInputChange={ (value) => onProductsInputChange(value) }
                                required
                                loading={ loadingProductsOptions }
                            />
                        </Box>
                    </Stack>
                </DialogContent>

                <Divider/>
                <DialogActions sx={ { px: 3, py: 2, gap: 2 } }>
                    <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                    <Button onClick={ onAddProducts }>Dodaj wybrane</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddProductsDialog;